import React, { useState } from "react";
import ResponsiveDialog from "./ResponsiveDialog";
import { Button, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

export interface BulkTextInputDialogProps {
  onClose: () => void;
  onSubmit: (values: string[]) => void;
  title?: string;
  description?: string;
  initialValues?: (string | number)[];
}

export function BulkTextInputDialog(props: BulkTextInputDialogProps): JSX.Element {
  const { i18n } = useTranslation();
  const [rawText, setRawText] = useState<string>(props.initialValues ? valuesToCSV(props.initialValues) : "");

  // Convert an array of strings or numbers into a CSV string
  function valuesToCSV(initialValues: (string | number)[]): string {
    const values = initialValues.map(v => {
      const s = String(v ?? '');
      if (/[,"\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    }).join(', ');
    return values;
}

  function handleSubmit() {
    // Replace all double double-quotes ("") with a comma to act as a delimiter
    const normalizedValues = rawText.replace(/""/g, ',');
    // Split the text by newlines, commas, tabs, and semicolons
    const values = normalizedValues.split(/[\n,\t;]/).map(value => {
      let val = value.trim();
      // Trim quotes, both " and ' are supported
      val = val.replace(/^['"]/, '').replace(/['"]$/, '');
      return val;
    }).filter(value => value.length > 0);

    // De-duplicate values
    const uniqueValues = Array.from(new Set(values));

    props.onSubmit(uniqueValues);
    props.onClose();
  }

  return (
    <ResponsiveDialog
      onClose={props.onClose}
      open={true}
      maxWidth="sm" >
      <DialogTitle>
        <Typography variant="h4">
          {props.title || i18n.t("query-form.bulk-add-values")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {props.description || i18n.t("query-form.bulk-add-values-description") }
        </Typography>
        <TextField
          autoFocus
          multiline
          fullWidth
          autoComplete="off"
          minRows={5}
          value={rawText}
          onChange={(e) => setRawText(e.target.value)} />
      </DialogContent>
      <DialogActions sx={{ p: 1 }} >
        <Button
          size="small"
          variant="contained"
          color="secondary"
          onClick={props.onClose} >
          { i18n.t("cancel") }
        </Button>
        <Button
          variant="contained"
          color='success'
          size="small"
          onClick={handleSubmit} >
          { i18n.t("submit") }
        </Button>
      </DialogActions>
    </ResponsiveDialog>
 );
}
